import { useEffect, useRef, useState } from 'react';
import ReactSelect, {
  ActionMeta,
  CSSObjectWithLabel,
  ControlProps,
  GroupBase,
  MultiValue,
  OptionProps,
  SingleValue,
  StylesConfig,
  components,
} from 'react-select';
import { Checkbox, MAGNETIC_TOKENS, css } from '@appd/uik';
import Select from 'react-select/dist/declarations/src/Select';

/* ******************************************************************************************************************
 *  UIK Theme tags
 * ****************************************************************************************************************** */

const {
  COLORS: { CONTROL_BORDER, CONTROL_BORDER_ACTIVE, DEFAULT_TEXT, DEFAULT_BG },
} = MAGNETIC_TOKENS;

/* ******************************************************************************************************************
 *  CustomOption component
 *    - Renders checkboxes in MultiSelect dropdown
 * ****************************************************************************************************************** */

const OptionSeparator = () => (
  <div
    // todo: tailwind
    style={{
      borderBottom: '1px solid rgba(36, 41, 46, 0.12',
      marginBottom: '2px',
    }}
  />
);

type OptionType = {
  value: string;
  label: string;
  shortLabel: string;
  option: {
    value: string;
    label: string;
    shortLabel: string;
  };
};

const CustomOption = ({
  getStyles,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  ...rest
}: OptionProps<OptionType, boolean, GroupBase<OptionType>>): JSX.Element => {
  const [_, setIsActive] = useState(false);
  const onMouseDown = () => setIsActive(true);
  const onMouseUp = () => setIsActive(false);
  const onMouseLeave = () => setIsActive(false);
  const backgroundColor = DEFAULT_BG;

  // clean up
  const style = {
    alignItems: 'center',
    color: DEFAULT_TEXT,
    display: 'flex',
    height: '30px',
    backgroundColor: isSelected
      ? backgroundColor
      : isFocused && children !== 'Select all'
      ? DEFAULT_BG
      : backgroundColor,
  };

  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style,
  };

  if (children === 'Separator') {
    return <OptionSeparator />;
  }
  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={props}
    >
      <Checkbox checked={isSelected} onChange={() => null} />
      {children}
    </components.Option>
  );
};

/* ******************************************************************************************************************
 *  MultiSelect Component
 *
 *    Input:
 *        options: {value, label, shortLabel: optional} - if specified, shortLabel is used for displaying in dropdown container
 *        value - default values
 *        showMaxItems - maximum number of items showed in dropdown container before it switches to "Select(x)" label
 *    Output:
 *        onChange(value, actionMeta) - is called when the user makes any changes
 *
 * ****************************************************************************************************************** */

interface MultiSelectProps {
  label?: string;
  options: any[];
  value: any;
  showMaxItems?: number;
  className?: string;
  onChange: (value: any, actionMeta: any) => void;
}

type SelectComponent = Select<OptionType, true, GroupBase<OptionType>>;

export const MultiSelect = ({ options, value, showMaxItems = 3, className, ...rest }: MultiSelectProps) => {
  const [input, setInput] = useState('');
  const [isSelectAllSelected, setIsSelectAllSelected] = useState(false);
  const selectOptions = Array.isArray(options) ? [...options] : [];
  const selectRef = useRef<SelectComponent>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const SELECT_ALL_OPTION = {
    value: 'selectedItems',
    label: 'Select All',
    shortLabel: 'Select all',
  };

  const ALL_OPTION = {
    value: 'all',
    label: 'All',
    shortLabel: 'All',
  };

  const onInputChange = (str: string, { action }: { action: string }): boolean => {
    if (action === 'set-value') return false;
    setInput(str);
    return true;
  };

  const isAllSelected = () =>
    (Array.isArray(valueRef.current) && valueRef.current.some(val => val === ALL_OPTION.value)) ||
    valueRef.current === ALL_OPTION.value;

  const isOptionSelected = (option: { value: string }) => {
    if (option.value === SELECT_ALL_OPTION.value) {
      return isSelectAllSelected;
    }
    if (Array.isArray(valueRef.current)) {
      return valueRef.current.some(value => value === option.value);
    } else {
      return valueRef.current == option.value || (isSelectAllSelected && valueRef.current !== ALL_OPTION.value);
    }
  };

  const setPlaceholder = (str: string) => {
    if (selectRef?.current?.inputRef) {
      (selectRef.current as any).inputRef.placeholder = str;
    }
  };

  const getOptions = [...selectOptions];

  let selectedOptions: unknown[] = [];
  if (Array.isArray(selectOptions)) {
    selectedOptions = Array.isArray(value)
      ? value.map(v => selectOptions.find(o => o.value === v))
      : selectOptions.find(o => o.value === value);
  }

  const getValue = () => {
    if (isAllSelected()) {
      return [ALL_OPTION];
    } else if (isSelectAllSelected) {
      return [SELECT_ALL_OPTION, ...selectOptions];
    } else {
      return selectedOptions;
    }
  };

  const selectedValues = getValue();

  // -------------------------------------------------------------------------------------------------
  // onChange - calls consumer's rest.onChange() with changes
  // -------------------------------------------------------------------------------------------------

  // todo: move to constants
  const SELECT_OPTION = 'select-option';
  const DESELECT_OPTION = 'deselect-option';
  const REMOVE_VALUE = 'remove-value';
  const CLEAR = 'clear';

  // todo: refactor this, add tests
  const onChange = (
    newValue: MultiValue<OptionType> | OptionType[] | SingleValue<OptionType>,
    actionMeta: ActionMeta<OptionType>,
  ): void => {
    const { action, option, removedValue } = actionMeta;
    if (action === SELECT_OPTION && option?.value === SELECT_ALL_OPTION.value) {
      setIsSelectAllSelected(true);
      rest.onChange(selectOptions, actionMeta);
    } else if (action === SELECT_OPTION && option?.value === ALL_OPTION.value) {
      setIsSelectAllSelected(false);
      rest.onChange(ALL_OPTION, actionMeta);
    } else if (
      (action === DESELECT_OPTION && option?.value === SELECT_ALL_OPTION.value) ||
      (action === REMOVE_VALUE && removedValue.value === SELECT_ALL_OPTION.value)
    ) {
      setIsSelectAllSelected(false);
      rest.onChange(ALL_OPTION, actionMeta);
    } else if (action === CLEAR) {
      setIsSelectAllSelected(false);
      selectRef && selectRef?.current?.focus();
      rest.onChange([], actionMeta);
    } else if (actionMeta.action === DESELECT_OPTION && isSelectAllSelected) {
      setIsSelectAllSelected(false);
      rest.onChange(
        selectOptions.filter(({ value }) => value !== option?.value),
        actionMeta,
      );
    } else if (actionMeta.action === DESELECT_OPTION && Array.isArray(newValue) && newValue.length === 0) {
      setIsSelectAllSelected(false);
      rest.onChange([], actionMeta);
    } else {
      const allOptionIndex = (newValue as OptionType[])?.indexOf(ALL_OPTION as OptionType);
      if (allOptionIndex > -1) {
        (newValue as OptionType[])?.splice(allOptionIndex, 1);
      }

      if (action === SELECT_OPTION && Array.isArray(newValue) && newValue.length === selectOptions.length) {
        const selectedCount = newValue.reduce((total, option) => {
          return (total += option.value !== SELECT_ALL_OPTION.value && option.value !== ALL_OPTION.value ? 1 : 0);
        }, 0);
        if (selectedCount === selectOptions.length) {
          // setIsSelectAllSelected(true);
        }
      }
      rest.onChange(newValue || [], actionMeta);
    }
  };

  // -------------------------------------------------------------------------------------------------
  // multiValueContainer - custom rendering selected values in MultiSelect's container
  // -------------------------------------------------------------------------------------------------

  interface multiValueContainerProps {
    selectProps: any;
    data: any;
  }

  const multiValueContainer = ({ selectProps, data }: multiValueContainerProps) => {
    if (selectProps.menuIsOpen) {
      return undefined;
    }

    const displayLabel = data.shortLabel ? data.shortLabel : data.label;
    const value = data.value;
    const selectedOptions = selectProps.value;

    if (value === ALL_OPTION.value) return displayLabel;
    if (value === SELECT_ALL_OPTION.value) {
      return selectedOptions.length === selectOptions.length || selectedOptions.length > showMaxItems
        ? `Selected (${selectedOptions.length - 1})`
        : '';
    }
    if (Array.isArray(selectedOptions)) {
      const index = selectedOptions.findIndex(selected => selected.value === value);
      const isCurrentOptionLast = index === selectedOptions.length - 1;
      const labelSuffix = isCurrentOptionLast ? '' : ', ';
      if (selectedOptions.length > showMaxItems) {
        return index === 0 ? `Selected (${selectedOptions.length})` : '';
      }
      return `${displayLabel}${labelSuffix}`;
    }
    return displayLabel;
  };

  // -------------------------------------------------------------------------------------------------
  // customStyle - styles for various React Select component
  // -------------------------------------------------------------------------------------------------

  type OptionType = {
    value: string;
    label: string;
    shortLabel: string;
    option: {
      value: string;
      label: string;
      shortLabel: string;
    };
  };

  const STYLES: StylesConfig<OptionType, boolean, GroupBase<OptionType>> = {
    input: () => ({
      color: DEFAULT_TEXT,
      minWidth: '20%',
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      color: DEFAULT_TEXT,
      background: DEFAULT_BG,
      textOverflow: 'ellipsis',
      maxWidth: '90%',
      paddingRight: 0,
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      display: 'flex',
      height: '20px',
    }),
    control: (base: CSSObjectWithLabel, props: ControlProps<OptionType, boolean, GroupBase<OptionType>>) => ({
      ...base,
      color: DEFAULT_TEXT,
      background: DEFAULT_BG,
      borderColor: props.isFocused ? CONTROL_BORDER_ACTIVE : CONTROL_BORDER,
      '&:hover': {
        borderColor: CONTROL_BORDER_ACTIVE,
      },
      borderWidth: '2px',
      height: '34px',
      minHeight: '34px',
      padding: '0 8px',
      lineHeight: '34px',
      alignItems: 'center',
    }),
    indicatorsContainer: (provided: CSSObjectWithLabel) => ({
      ...provided,
      height: '32px', // Set the height of the indicator container to 32px
      lineHeight: '32px', // Adjust the line-height as needed
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      borderRadius: 1,
      color: DEFAULT_TEXT,
      backgroundColor: DEFAULT_BG,
      border: '0',
      boxShadow: '0 10px 30px 0 #000',
      width: 'calc(100% - 1em)',
      zIndex: 10,
    }),
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '4px',
        flex: '1 0 0',
      }}
    >
      {rest.label ? (
        <div>
          <label
            style={{
              color: 'var(--base-text-default, #23282E)',
              fontFamily: 'var(--typography-font-body)',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '20px',
            }}
          >
            {rest.label}
          </label>
        </div>
      ) : null}

      <div style={{ width: '100%' }}>
        <ReactSelect
          ref={selectRef}
          isOptionSelected={isOptionSelected}
          options={getOptions}
          value={selectedValues}
          blurInputOnSelect={false} // blurInput, inputValue and onInputChange are used for saving the input while searching and selecting
          inputValue={input}
          onInputChange={onInputChange}
          onFocus={() => {}}
          onChange={onChange}
          onMenuClose={() => {
            setPlaceholder('');
          }}
          openMenuOnFocus={true}
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          isClearable
          isMulti
          isSearchable
          styles={STYLES}
          placeholder={'Select ...'}
          components={{
            MultiValueContainer: multiValueContainer,
            Option: CustomOption,
          }}
          className={className}
        />
      </div>
    </div>
  );
};
